import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { scrollToId, waLink } from "@/lib/site";

const ease = [0.22, 1, 0.36, 1];

const PriceChip = ({ testid, label, price, unit, className, delay, target }) => (
  <motion.button
    data-testid={testid}
    onClick={() => scrollToId(target)}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay, ease }}
    whileHover={{ y: -4 }}
    className={`absolute z-20 text-left bg-[#FAF8F5]/90 backdrop-blur-xl border border-white/60 shadow-[0_20px_50px_-15px_rgba(18,21,24,0.35)] px-5 py-4 ${className}`}
  >
    <span className="font-mono-d text-[10px] tracking-[0.25em] text-[#0F4C3A] block">{label}</span>
    <span className="font-serif-d text-2xl md:text-3xl font-semibold text-[#121518] block leading-tight">{price}</span>
    {unit && <span className="text-xs text-[#525860]">{unit}</span>}
  </motion.button>
);

export const Hero = ({ onPick }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} className="relative pt-[110px] md:pt-[130px] pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-6 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono-d text-[11px] tracking-[0.3em] text-[#0F4C3A] mb-8 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C59B27]" /> EMPRESAS · SÃO PAULO
          </motion.p>
          <MaskLines
            testid="hero-headline"
            className="font-serif-d text-[2.9rem] leading-[0.95] sm:text-7xl lg:text-[5.6rem] font-medium tracking-[-0.02em] text-[#121518]"
            lines={["Limpeza comercial", "para escritórios,", <>clínicas e <em className="text-[#0F4C3A]">empresas.</em></>]}
          />
          <motion.p data-testid="hero-subtitle" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease }}
            className="mt-8 max-w-md text-base md:text-lg text-[#525860] leading-relaxed">
            Seu ambiente de trabalho limpo e organizado, sem precisar manter uma equipe própria de limpeza.
            Atendimento recorrente com frequência e horários combinados. Também fazemos{" "}
            <button onClick={() => scrollToId("faxina-residencial")} className="text-[#121518] font-semibold underline decoration-[#C59B27] underline-offset-4">faxina residencial</button> e{" "}
            <button onClick={() => scrollToId("limpeza-pos-obra")} className="text-[#121518] font-semibold underline decoration-[#C59B27] underline-offset-4">limpeza pós-obra</button>.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap gap-3">
            <button data-testid="hero-cta-orcamento" onClick={() => onPick("comercial")}
              className="group rounded-full bg-[#121518] text-[#FAF8F5] pl-7 pr-2 py-2 flex items-center gap-4 hover:bg-[#0F4C3A] transition-colors">
              Solicitar orçamento comercial
              <span className="w-10 h-10 rounded-full bg-[#C59B27] flex items-center justify-center transition-transform duration-500 group-hover:rotate-[-45deg]">
                <ArrowDownRight size={18} className="text-[#121518]" />
              </span>
            </button>
            <a data-testid="hero-cta-whatsapp" href={waLink("Olá! Gostaria de uma proposta de limpeza comercial para a minha empresa.")} target="_blank" rel="noreferrer"
              className="rounded-full border border-[#121518]/20 px-7 py-4 text-sm hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors">
              WhatsApp 11 99457-6696
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative" style={{ perspective: 1200 }} onMouseMove={onMove}
          onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 0 0)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 0 0)" }}
            transition={{ duration: 1.5, delay: 0.3, ease }}
            className="relative mx-auto w-full max-w-[520px] aspect-[4/5] overflow-hidden rounded-t-full grain">
            <motion.img src="/img/escritorio.jpg" alt="Escritório limpo e organizado" style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121518]/40 via-transparent to-transparent" />
          </motion.div>
          <PriceChip testid="hero-badge-planos" label="PLANOS COMERCIAIS" price="Semanal, diário" unit="ou contrato mensal/anual"
            target="planos-comerciais" className="left-0 sm:left-4 lg:-left-6 bottom-24" delay={1.2} />
          <PriceChip testid="hero-badge-horario" label="HORÁRIOS FLEXÍVEIS" price="Antes ou depois" unit="do expediente, conforme combinado"
            target="limpeza-comercial" className="right-0 sm:right-4 lg:-right-2 bottom-4" delay={1.35} />
          <motion.svg initial={{ rotate: 0 }} animate={{ rotate: 360 }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            viewBox="0 0 200 200" className="hidden sm:block absolute -top-4 right-6 lg:right-10 w-28 h-28 z-20" aria-hidden="true">
            <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
            <circle cx="100" cy="100" r="98" fill="#0F4C3A" />
            <text fill="#FAF8F5" fontSize="12" letterSpacing="3.4" fontFamily="JetBrains Mono">
              <textPath href="#circ">LIMPEZA RÁPIDA · EMPRESAS E RESIDÊNCIAS ·</textPath>
            </text>
            <path d="M100 78c2 10 4 12 14 14-10 2-12 4-14 14-2-10-4-12-14-14 10-2 12-4 14-14z" fill="#C59B27" />
          </motion.svg>
        </div>
      </div>
    </section>
  );
};
